import { Card, type CardProps } from '@mui/material';
import { forwardRef, type KeyboardEvent, type MouseEvent } from 'react';

/**
 * @tier atom
 *
 * Card surface. Inherits all visual treatment (border, radius, padding,
 * shadow, hover state, internal flex layout) from the theme override
 * `MuiCard` slot — see `src/theme/components.ts`.
 *
 * Internal layout: the card is a vertical flex container with a 20px gap
 * and the last child pinned to the bottom (margin-top: auto). This lets a
 * consumer compose `[icon, body, status]` and have the status footer sit
 * at the bottom of the card without any inline styling.
 *
 * Composition guidance (e.g. the AI Agents page):
 *   <VyCard>
 *     <VyIconTile color="purple"><Lightning /></VyIconTile>
 *     <VyStack gap={1}>
 *       <VyTypography variant="body1Medium">Title</VyTypography>
 *       <VyTypography variant="caption" color="text.secondary">…</VyTypography>
 *     </VyStack>
 *     <VyTypography variant="caption" color="text.secondary">Active</VyTypography>
 *   </VyCard>
 *
 * ### Making a card clickable
 *
 * Pass `interactive` and either `onClick` (in-app action) or render the
 * card as an anchor for navigation:
 *
 *   <VyCard interactive onClick={openSomething}>...</VyCard>
 *
 *   <VyCard interactive component="a" href={url} target="_blank" rel="noopener noreferrer">
 *     ...
 *   </VyCard>
 *
 * The interactive treatment (cursor, focus ring, anchor reset) lives in
 * the `MuiCard` theme override and is keyed off `[role="button"], [href]`
 * — so both div-with-interactive and anchor cards get the same affordance
 * without any per-call-site styling.
 */
export interface VyCardProps extends CardProps {
  /**
   * Render the card as a clickable surface. Adds `role="button"`,
   * `tabIndex=0`, and Enter/Space keyboard activation. Theme override
   * paints the cursor/focus/hover treatment.
   *
   * Ignored (and unnecessary) when the card is rendered as an anchor via
   * `component="a"` — anchors are interactive by nature; the theme
   * override matches `[href]` to give them the same look.
   *
   * Default: `false`.
   */
  interactive?: boolean;
}

export const VyCard = forwardRef<HTMLDivElement, VyCardProps>(
  function VyCard({ interactive, onClick, onKeyDown, ...rest }, ref) {
    const isAnchor = rest.component === 'a' || 'href' in rest;
    const enableButtonSemantics = interactive && !isAnchor;

    const handleKeyDown = enableButtonSemantics
      ? (event: KeyboardEvent<HTMLDivElement>) => {
          onKeyDown?.(event);
          if (event.defaultPrevented) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick?.(event as unknown as MouseEvent<HTMLDivElement>);
          }
        }
      : onKeyDown;

    return (
      <Card
        ref={ref}
        role={enableButtonSemantics ? 'button' : undefined}
        tabIndex={enableButtonSemantics ? 0 : undefined}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        {...rest}
      />
    );
  },
);
